import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import AddAttachmentModal from 'components/add-attachment-modal/AddAttachmentsModal';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useUploadJobImageFileMutation } from 'containers/jobs/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useEffect, useRef, useState } from 'react';
import {
  modifyJobAttachment,
  removeJobAttachments,
  resetJobAttachmentState,
  selectJobAttachments,
  selectJobRelatedEntityData,
  updateJobAttachments
} from '../createJobSlice';
import { VoidFn } from 'types/baseTypes';
import { FormEditIcon } from 'assets/icons';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { JobAttachmentType } from 'containers/jobs/jobs.model';
import ItemsTabReactIcon from 'assets/react-icons/ItemsTabReactIcon';
import { useLazyGetInvoiceAttachmentsQuery } from 'containers/invoices/api';
import { useLazyGetEstimateAttachmentsQuery } from 'containers/estimates/api';
import AttachmentLibrary from 'components/attachment-library/AttachmentLibrary';
import { generateAttachmentSections } from 'containers/estimates/utils';

interface JobAttachmentsSectionProps {
  className?: string;
  isEdit?: boolean;
  handleOnFocus: VoidFn;
}

const JobAttachmentsSection: FC<JobAttachmentsSectionProps> = ({ className, isEdit = false, handleOnFocus }) => {
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState({
    isEdit: false,
    open: false,
    attachment: { id: null, url: null, title: null, imagePreview: null }
  });
  const [isAttachmentLibraryOpen, setIsAttachmentLibraryOpen] = useState(false);

  const currentImageUploadCall = useRef(null);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const jobAttachments = useAppSelector(selectJobAttachments);
  const { invoiceId, estimateId } = useAppSelector(selectJobRelatedEntityData);
  const dispatch = useAppDispatch();

  const [uploadFileMutation, { isLoading: isUploadingImage }] = useUploadJobImageFileMutation();
  const [getInvoiceAttachmentListQuery, { isFetching: isInvoiceAttachmentListFetching, data: invoiceAttachments }] =
    useLazyGetInvoiceAttachmentsQuery();

  const [getEstimateAttachmentsQuery, { isFetching: isEstimateAttachmentsFetching, data: estimateAttachments }] =
    useLazyGetEstimateAttachmentsQuery();

  useEffect(() => {
    if (invoiceId) {
      getInvoiceAttachmentListQuery({ invoiceId, teamId: currentTeam.id });
    }
  }, [invoiceId, currentTeam.id]);

  useEffect(() => {
    if (estimateId) {
      getEstimateAttachmentsQuery({ teamId: currentTeam.id, estimateId: estimateId });
    }
  }, [estimateId, currentTeam.id]);

  const handleOpenAttachmentLibrary = () => {
    setIsAttachmentLibraryOpen(true);
  };

  const handleAddPhotoClick = () => {
    setIsAttachmentModalOpen({
      isEdit: false,
      open: true,
      attachment: { id: null, url: null, title: null, imagePreview: null }
    });
    handleOnFocus();
  };

  const resetAttachmentModalState = () => {
    setIsAttachmentModalOpen({
      isEdit: false,
      open: false,
      attachment: { id: null, url: null, title: null, imagePreview: null }
    });
  };

  const handleAttachmentModalClose = () => {
    if (isUploadingImage) {
      currentImageUploadCall.current.abort();
    }
    resetAttachmentModalState();
  };

  const handleAttachmentSave = async ({ image, title, imagePreview, hasImageChanged }) => {
    if (isAttachmentModalOpen.isEdit) {
      let url = isAttachmentModalOpen.attachment.url;
      if (hasImageChanged) {
        const { data } = await getImageURL(image);
        url = data.imageUrl;
      }
      dispatch(
        modifyJobAttachment({
          title,
          url,
          imagePreview,
          id: isAttachmentModalOpen.attachment.id,
          urlLink: imagePreview
        })
      );
      resetAttachmentModalState();
      return;
    }
    const { data } = await getImageURL(image);
    dispatch(updateJobAttachments({ title, url: data.imageUrl, imagePreview, id: null, urlLink: imagePreview }));
    resetAttachmentModalState();
  };

  const getImageURL = async imageFile => {
    try {
      const imageUrlFromS3Call = uploadFileMutation({
        file: imageFile,
        teamId: currentTeam.id
      });
      currentImageUploadCall.current = imageUrlFromS3Call;
      const imageUrlFromS3 = imageUrlFromS3Call.unwrap();
      return imageUrlFromS3;
    } catch (err) {
      //
    }
  };

  const onEditAttachment = (attachment: JobAttachmentType) => {
    setIsAttachmentModalOpen({ attachment: attachment, open: true, isEdit: true });
  };

  const onRemoveAttachment = (attachment: JobAttachmentType) => {
    dispatch(removeJobAttachments(attachment));
  };

  const handleSaveFromLibrary = attachments => {
    dispatch(resetJobAttachmentState());
    const attachmentsToSave = attachments.map(attachment => ({
      title: attachment.title,
      url: attachment.url,
      imagePreview: attachment.urlLink,
      id: attachment.id,
      source: attachment.source,
      urlLink: attachment.urlLink
    }));
    attachmentsToSave.forEach(attachment => {
      dispatch(updateJobAttachments(attachment));
    });
    setIsAttachmentLibraryOpen(false);
  };

  return (
    <div className={className}>
      <div className="mb-2 text-17px font-semibold text-heading">Attachments</div>
      <div className="flex flex-col gap-5">
        {jobAttachments?.map(attachment => {
          return (
            <div
              onClick={() => onEditAttachment(attachment)}
              key={attachment.url}
              className="relative flex min-h-[100px] w-full flex-row 
              rounded border border-borderGray py-4 px-5 last:mb-5 ">
              <div className="relative mr-4 h-16 w-16 rounded-md">
                <img
                  className="absolute left-1/2 h-full -translate-x-1/2 rounded-md object-cover"
                  src={attachment.imagePreview}
                />
              </div>

              <div className="mb-[6px] break-words text-17px font-semibold text-heading line-clamp-2">
                {attachment.title}
              </div>

              <FormCloseReactIcon
                id="create-invoice-remove-attachment-close-icon"
                onClick={e => {
                  e.stopPropagation();
                  onRemoveAttachment(attachment);
                }}
                className="absolute top-3 right-[-25px] shrink-0 cursor-pointer 1439px:right-[-30px]"
              />
              <FormEditIcon
                id="create-invoice-attachment-edit-icon"
                onClick={() => onEditAttachment(attachment)}
                className="absolute bottom-3 right-[-25px] mt-3 shrink-0 cursor-pointer 1439px:right-[-30px]"
              />
            </div>
          );
        })}
      </div>
      <div
        className="flex w-full cursor-pointer flex-row 
        items-center justify-between rounded border border-borderGray py-4 px-5">
        <div className="flex flex-row gap-2" onClick={handleAddPhotoClick}>
          <LineItemPlusReactIcon id="create-invoice-add-photo-icon" className="shrink-0" />
          <div id="job-add-photo" className="text-17px font-semibold text-secondary">
            Add photo
          </div>
        </div>
        {(estimateId || invoiceId) && (
          <div className="flex cursor-pointer items-center gap-2" onClick={handleOpenAttachmentLibrary}>
            <ItemsTabReactIcon
              id="add-from-library"
              className="path-fill-current rect-stroke-current mr-2 text-secondary"
            />
            <div className="text-17px font-semibold text-secondary">Add from library</div>
          </div>
        )}
      </div>
      {isAttachmentModalOpen.open && (
        <AddAttachmentModal
          isEdit={isAttachmentModalOpen.isEdit}
          isUploadingImage={isUploadingImage}
          onClose={handleAttachmentModalClose}
          onSave={handleAttachmentSave}
          attachmentData={isAttachmentModalOpen.attachment}
        />
      )}
      {isAttachmentLibraryOpen && (
        <AttachmentLibrary
          onClose={() => setIsAttachmentLibraryOpen(false)}
          onSave={handleSaveFromLibrary}
          isOpen={isAttachmentLibraryOpen}
          sections={generateAttachmentSections(estimateAttachments, null, invoiceAttachments)}
          selectedAttachments={jobAttachments}
        />
      )}
    </div>
  );
};

export default JobAttachmentsSection;
